import type { FilterDefinitionValueRestriction } from '@carrefour-gcs/bff-sdk';
import type { FilterValueRestrictionStateType } from '../types/filterValueRestrictionState.type';

/**
 * Tells whether the trigger value of a restriction is part of the current selection of its filter.
 * An empty selection means every value is selected, so the trigger value is included.
 */
const isTriggerSelected = (restriction: FilterDefinitionValueRestriction, selection: unknown): boolean => {
  if (!selection || (Array.isArray(selection) && selection.length === 0)) {
    return true;
  }

  if (Array.isArray(selection)) {
    return selection.includes(restriction.value);
  }

  return selection === restriction.value;
};

/**
 * Evaluates the restrictions of an option against the current form values. The option is disabled
 * as soon as one restriction applies, and the reason of the first applying restriction is returned.
 *
 * @param restrictions Restrictions of the option, already scoped to the user country by the BFF.
 * @param formValues Current values of the form, keyed by filter definition name.
 * @returns Whether the option is disabled, and the i18n key explaining why.
 *
 * @example
 * resolveFilterValueRestriction([{ field: 'activity_type', value: 'E-Commerce', disabledReasonTranslationKey: 'key' }], {
 *   activity_type: ['E-Commerce']
 * }); // { disabled: true, disabledReasonTranslationKey: 'key' }
 */
export const resolveFilterValueRestriction = (
  restrictions: FilterDefinitionValueRestriction[] | undefined,
  formValues: Record<string, unknown>
): FilterValueRestrictionStateType => {
  const applyingRestriction = restrictions?.find((restriction) =>
    isTriggerSelected(restriction, formValues[restriction.field])
  );

  return {
    disabled: applyingRestriction !== undefined,
    disabledReasonTranslationKey: applyingRestriction?.disabledReasonTranslationKey ?? null
  };
};
